// Docs
//   Props
//     -requestFunc
//        Function. Function to call on button click. Do not include function parentheses - add args in next prop. Button will enter waiting state until function returns. Exceptions thrown by function will set button's error state.
//     -requestArgs
//        Array. Arguments to pass into requestFunc.
//     -type
//        String. HTML button type. Options: 'none', 'download', 'submit'
//     -defaultButtonText
//        String. Text for button body in ready state.
//     -waitingButtonText
//        String. Text for button body in waiting/loading state.
//     -errorButtonText
//        String. Text for button body in error state.
//     -defaultTooltipText
//        String. Text for tooltip in ready state.
//     -errorTooltipText
//        String. Text for tooltip in error state.
//     -iconOnly
//        Boolean. True = show icon only. False = show text and icon (if icon prop set).
//     -icon
//        JSX component. Pass in a JSX component for icon, eg icon={<RefreshIcon/>}.
//     -style
//        Object. Style override
//     -disabled
//        Boolean. Start disabled?
//     -disableOverride
//        Stateful var. Control disabled state from parent component. Pass in a stateful boolean var

import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import ErrorIcon from "@mui/icons-material/Error";
import Tooltip from "@mui/material/Tooltip";
import CachedIcon from "@mui/icons-material/Cached";
import { Button, Typography } from "@mui/material";

// Set default props
DownloadButton.defaultProps = {
    type: "button",
    waitingButtonText: "Loading",
    errorButtonText: "Failed",
    defaultTooltipText: "",
    iconOnly: false,
};

export default function DownloadButton(props) {
    const { requestFunc, requestArgs, type, defaultButtonText, waitingButtonText, errorButtonText, defaultTooltipText, iconOnly, icon, style, disabled, disableOverride } = props
    let iconOnlyProp;
    if (iconOnly == undefined) {
        iconOnlyProp = false;
    } else {
        iconOnlyProp = iconOnly;
    }

    const [buttonState, setButtonState] = useState("ready");
    const [isWaiting, setIsWaiting] = useState(false);
    const [isDisabled, setIsDisabled] = useState(disabled);
    const [error, setError] = useState(false);
    const [info, setInfo] = useState(false);
    const [tooltipText, setTooltipText] = useState(defaultTooltipText);
    const [buttonText, setButtonText] = useState(defaultButtonText);
    const [forceTooltipVisible, setForceTooltipVisible] = useState(false);
    const [errorTooltipText, setErrorTooltipText] =
        useState("Unspecified error.");
    const [infoTooltipText, setInfoTooltipText] = useState("");

    ////////// STYLING for BUTTON
    let bgColor;
    let disableDropShadow = false;
    // Styling if we have an icon-only dl button
    if (iconOnlyProp != undefined) {
        if (iconOnlyProp == true) {
            bgColor = "rgba(0,0,0,0)";
            disableDropShadow = true;
        } else {
            bgColor = "green";
        }
    }

    const baseStyle = {
        paddingLeft: "20px",
        paddingRight: "20px",
        backgroundColor: bgColor,
        color: "white",
        textTransform: "none",
        fontWeight: "bold",
        outline: "none",
        ...style,
    };

    let iconOnlyStyle = {
        borderRadius: "50%",
        // overwrite <Button>'s default min dimensions
        minHeight: "42px",
        minWidth: "42px",
        height: "64px",
        width: "64px",
        backgroundColor: "transparent",
        outline: "none",
        ...style,
    };

    const waitingStyle = {
        backgroundColor: "rgba(107, 164, 125, 0.2)",
        color: "green",
        textTransform: "none",
        fontWeight: "bold",
        outline: "1px solid green",
        ...style,
    };
    const disabledStyle = {
        color: "grey",
    };
    const errorStyle = {
        backgroundColor: "rgb(180 10 10)",
    };

    // Final style mixer for button
    // Styles lower in each 'finalStyle' list will override those higher in list
    let finalStyle;
    if (isWaiting && iconOnlyProp) {
        finalStyle = {
            ...baseStyle,
            ...waitingStyle,
            ...iconOnlyStyle,
        };
    } else if (isWaiting) {
        finalStyle = {
            ...baseStyle,
            ...waitingStyle,
        };
    } else if (iconOnlyProp) {
        finalStyle = {
            ...baseStyle,
            ...iconOnlyStyle,
        };
    } else if (iconOnlyProp == false && error) {
        finalStyle = {
            ...baseStyle,
            ...errorStyle,
        };
    } else {
        finalStyle = baseStyle;
    }
    if (isDisabled) {
        finalStyle = { ...finalStyle, ...disabledStyle };
    }

    // SX for TOOLTIP
    let tooltipSxStandard = {
        bgcolor: "grey",
        color: "white",
        zIndex: 0,
        left: "-20px",
    };

    let tooltipSxError = {
        bgcolor: "red",
        color: "white",
        zIndex: 0,
        left: "-20px",
    };

    // Style mixer for tooltip
    let finalTooltipProperties;
    finalTooltipProperties = {
        sx: tooltipSxStandard,
    };

    if (error) {
        finalTooltipProperties = {
            sx: tooltipSxError,
        };
    }

    // async function handleDL(filename, url, status){
    //     // Begin timer to ensure loading animation plays for a minimum time to avoid it looking buggy
    //     let startTime = new Date();

    //     // Check if the URL we have above points to a file that is available for download. If not, show error
    //     // 'status' it not a required prop, however ommitting it will disable error display
    //     if (status == '200' || status==undefined){
    //       // Do we need to show the download security warning?
    //       // If they have no warndlsecurity property, show the warning
    //       if (userDetails.hasOwnProperty('warndlsecurity') == false){
    //         setOpenDownloadDialog(true)
    //       } else if (userDetails['warndlsecurity'] == false){
    //         // If they do have the property and it is false
    //         // Initiate download immediately using the onClick function passed into DownloadButton
    //         downloadClick()
    //         // Otherwise
    //       }else if(userDetails['warndlsecurity'] == true){
    //         setIconButtonDisabled(true)
    //         // Show download warning page
    //         setOpenDownloadDialog(true)
    //       }
    //     }
    //     // else if the status of our download is not 200...
    //     else{
    //       setDownloadError(true)
    //       if (status == '404'){
    //         if (iconOnly == true){
    //           setDownloadErrorText('File not found')
    //         }
    //         if (setButtonText != undefined){
    //           setButtonText('File not found')
    //         }
    //       }else{
    //         if (iconOnly == true){
    //           setDownloadErrorText('Unspecified download error')
    //         }
    //         if (setButtonText != undefined){
    //           setButtonText('Unspecified download error')
    //         }
    //       }
    //     }
    // }

    async function buttonClick(event) {
        // let funcError = false
        // try {
        setButtonState("wait");
        let result = await requestFunc(event, ...requestArgs);
        if (
            result["status"] == "error" ||
            result["status"] == "info" ||
            result["status"] == "success"
        ) {
            setButtonState(result["status"]);
            setTooltipText(result["message"]);
        } else {
            setButtonState("error");
            setTooltipText("Unexpected error.");
        }

        // }
        // catch (e){

        //     setErrorTooltipText(e.toString())
        //     setButtonState('error')
        // }
    }

    useEffect(() => {
        if (buttonState == "ready") {
            setIsWaiting(false);
            setButtonText(defaultButtonText);
            setError(false);
            setIsDisabled(false);
            setTooltipText(defaultTooltipText);
            setForceTooltipVisible(false);
        } else if (buttonState == "wait") {
            setIsWaiting(true);
            setButtonText(waitingButtonText);
            setError(false);
            setIsDisabled(true);
            setTooltipText(``);
            setForceTooltipVisible(false);
        } else if (buttonState == "error") {
            setIsWaiting(false);
            setError(true);
            setButtonText(errorButtonText);
            // setTooltipText(`${errorTooltipText}.  Click to try again.`)
            setIsDisabled(false);
            setForceTooltipVisible(true);
        } else if (buttonState == "info") {
            setIsWaiting(false);
            setError(false);
            setInfo(true);
            setIsDisabled(false);
            setForceTooltipVisible(true);
        }
    }, [buttonState]);

    // If parent prop is attempting to override the disabled status, allow it to
    useEffect(() => {
        if (disableOverride != undefined) {
            setIsDisabled(disableOverride);
        }
    }, [disableOverride, isDisabled]);

    return (
        <Tooltip
            title={tooltipText}
            open={forceTooltipVisible}
            placement="right"
            arrow
            PopperProps={{ style: { zIndex: 0 } }}
            componentsProps={{
                tooltip: {
                    sx: {
                        ...finalTooltipProperties.sx,
                    },
                },
                arrow: {
                    sx: {
                        color: finalTooltipProperties.sx.bgcolor,
                    },
                },
            }}
        >
            <span>
                <Button
                    onClick={buttonClick}
                    style={finalStyle}
                    disabled={isDisabled}
                    type={type}
                >
                    {iconOnlyProp == false && error == false && (
                        <>
                            <Typography sx={{ marginRight: "0.3em" }}>
                                {buttonText}
                            </Typography>
                            {icon}
                        </>
                    )}
                    {iconOnlyProp == false && error && (
                        <>
                            <Typography sx={{ marginRight: "0.3em" }}>
                                {buttonText}
                            </Typography>
                            <CachedIcon></CachedIcon>
                        </>
                    )}
                    {error == false && iconOnlyProp && icon}
                    {isWaiting && iconOnlyProp && (
                        <CircularProgress
                            size={45}
                            sx={{
                                color: "green",
                                position: "absolute",
                                zIndex: 1,
                            }}
                        />
                    )}
                    {isWaiting && iconOnlyProp == false && (
                        <LinearProgress
                            color="success"
                            sx={{
                                position: "absolute",
                                height: "100%",
                                width: "100%",
                                opacity: "0.2",
                                zIndex: 100,
                            }}
                        />
                    )}
                    {error && iconOnlyProp && (
                        <ErrorIcon color="error"></ErrorIcon>
                    )}
                </Button>
            </span>
        </Tooltip>
    );
}
